import {
    ArrowLeftOutlined, CheckCircleOutlined, DeleteOutlined, DownOutlined, EditOutlined, FileTextOutlined, LinkOutlined, OrderedListOutlined, ShoppingCartOutlined, StopOutlined,
} from '@ant-design/icons';
import {
    useMutation, useQuery,
} from '@tanstack/react-query';
import {
    Button, Card, Collapse, ConfigProvider, Flex, Grid, theme as AntdTheme, theme, Typography,
} from 'antd';
import {
    useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { Helmet } from 'react-helmet';
import {
    Link,
    useNavigate,
    useParams,
} from 'react-router-dom';

import {
    managerMutations, managerQueries, projectsMutations, projectsQueries,
} from '@/api';
import {
    ConfirmModal, DeleteProjectModal, getActionControlStyleVars, ProjectActiveBadge, ProjectScriptsTab, RoleGuard, ScriptChangesModal, ScriptChangesNotification,
} from '@/components';
import {
    AppRoute, DARK_THEME_HTML_BACKGROUND_COLOR, UserRole,
} from '@/constants';
import {
    useCustomParams, usePopupQueryParams,
} from '@/hooks';
import {
    useUIStore, useUserStore,
} from '@/stores';
import { lightTheme } from '@/themes';

enum ProjectDetailTabKey {
    Script = 'script',
    CrossSells = 'cross-sells',
    Regulation = 'regulation',
}

const ProjectDetailTabLabel = {
    [ProjectDetailTabKey.Script]: 'Скрипти',
    [ProjectDetailTabKey.CrossSells]: 'Крос-сейли',
} as const;

export const ProjectDetailsPage = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { user } = useUserStore();
    const { theme: currentTheme, setProjectCrmFloatUrl, setPageTitle } = useUIStore();
    const screens = Grid.useBreakpoint();
    const isMobileProjectLayout = screens.lg === false;
    const { token } = theme.useToken();

    const contentBg = useMemo(() => {
        return currentTheme === 'dark' ? DARK_THEME_HTML_BACKGROUND_COLOR : '#FAFAFA';
    }, [currentTheme]);

    const crmButtonTextColor = currentTheme === 'dark' ? '#FFFFFF' : 'rgba(0, 0, 0, 0.88)';

    const lightThemeToken = useMemo(() => {
        return AntdTheme.getDesignToken({
            algorithm: AntdTheme.defaultAlgorithm,
            ...lightTheme,
        });
    }, []);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [toggleModalOpen, setToggleModalOpen] = useState(false);
    const [activePanels, setActivePanels] = useState<string[]>([]);
    const [activeTab, setActiveTab] = useState<string>(ProjectDetailTabKey.Script);
    const [isHeaderVisible, setIsHeaderVisible] = useState(true);
    const [crmBtnHover, setCrmBtnHover] = useState(false);
    const headerRef = useRef<HTMLDivElement>(null);
    const popupQuery = usePopupQueryParams();

    useEffect(() => {
        const el = headerRef.current;
        if (!el) return;
        let root: Element | null = null;
        let node: HTMLElement | null = el.parentElement;
        while (node) {
            const { overflowY } = getComputedStyle(node);
            if (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay') {
                root = node;
                break;
            }
            node = node.parentElement;
        }
        const check = () => {
            const rect = el.getBoundingClientRect();
            if (root) {
                const rootRect = (root as Element).getBoundingClientRect();
                setIsHeaderVisible(rect.bottom > rootRect.top + 8);
            } else {
                setIsHeaderVisible(rect.bottom > 80);
            }
        };
        check();
        if (root) {
            (root as HTMLElement).addEventListener('scroll', check, { passive: true });
        } else {
            window.addEventListener('scroll', check, { passive: true });
        }
        const resizeObs = new ResizeObserver(check);
        resizeObs.observe(el);
        return () => {
            if (root) (root as HTMLElement).removeEventListener('scroll', check);
            else window.removeEventListener('scroll', check);
            resizeObs.disconnect();
        };
    }, []);

    const isManager = useMemo(() => user?.role === UserRole.Manager, [user?.role]);
    const { search } = useCustomParams();

    const projectQuery = useMemo(() => {
        return isManager
            ? managerQueries.getManagerProject(id || '', { search })
            : projectsQueries.getOneProject(id || '');
    }, [isManager, id, search]);

    const { data: project, isLoading } = useQuery(projectQuery);

    const deleteMutationOptions = useMemo(
        () => project
            ? projectsMutations.delete(project.id)
            : projectsMutations.delete(''),
        [project],
    );

    const { mutate: deleteProject, isPending: isDeleting } = useMutation(deleteMutationOptions);
    const { mutate: toggleProject, isPending: isToggling } = useMutation(
        projectsMutations.toggleActive(id || ''),
    );
    const { mutate: acknowledgeProjectChanges, isPending: isAcknowledgingChanges } = useMutation(
        managerMutations.acknowledgeProjectChanges(id || ''),
    );

    const handleDelete = useCallback(() => {
        if (project) {
            setDeleteModalOpen(true);
        }
    }, [project]);

    const handleDeleteConfirm = useCallback(() => {
        if (project) {
            deleteProject(undefined, {
                onSuccess: () => {
                    setDeleteModalOpen(false);
                    navigate(AppRoute.Projects);
                },
            });
        }
    }, [project, deleteProject, navigate]);

    const handleDeleteCancel = useCallback(() => {
        setDeleteModalOpen(false);
    }, []);

    const handleToggleConfirm = useCallback(() => {
        toggleProject(undefined, {
            onSuccess: () => setToggleModalOpen(false),
        });
    }, [toggleProject]);

    const handleToggleCancel = useCallback(() => {
        setToggleModalOpen(false);
    }, []);

    const unacknowledgedChanges = project?.unacknowledged_changes || [];
    const hasUnacknowledgedChanges = isManager && unacknowledgedChanges.length > 0;
    const latestChange = hasUnacknowledgedChanges ? unacknowledgedChanges[0] : null;
    const latestChangedAt = latestChange?.changed_at || null;
    const latestRegulationDiff = useMemo(() => {
        if (!latestChange?.changes?.length) {
            return null;
        }

        const regulationChange = latestChange.changes.find((change) => change?.field === 'regulation');
        if (!regulationChange) {
            return null;
        }

        const normalizeRegulationValue = (value: unknown): string => {
            if (typeof value !== 'string' || value.trim().length === 0) {
                return '—';
            }

            const tmp = document.createElement('div');
            tmp.innerHTML = value;
            const plainText = (tmp.textContent || tmp.innerText || '').replace(/\s+/g, ' ').trim();

            if (!plainText) {
                return '—';
            }

            return plainText.length > 160 ? `${plainText.slice(0, 160)}...` : plainText;
        };

        return {
            oldValue: normalizeRegulationValue(regulationChange.old_value),
            newValue: normalizeRegulationValue(regulationChange.new_value),
        };
    }, [latestChange]);

    const handleOpenRegulationFromAlert = useCallback(() => {
        if (!activePanels.includes(ProjectDetailTabKey.Regulation)) {
            setActivePanels((prevPanels) => [...prevPanels, ProjectDetailTabKey.Regulation]);
        }
        void popupQuery.open({ mode: 'changes' });
    }, [activePanels, popupQuery]);

    const handleAcknowledgeRegulationChanges = useCallback(() => {
        if (!id) {
            return;
        }

        acknowledgeProjectChanges(undefined, {
            onSuccess: () => {
                setActivePanels([ProjectDetailTabKey.Regulation]);
                void popupQuery.close();
            },
        });
    }, [acknowledgeProjectChanges, id, popupQuery]);

    const handleCloseChangesModal = useCallback(() => {
        void popupQuery.close();
    }, [popupQuery]);

    const openCrmUrl = useCallback(() => {
        if (project?.url) window.open(project.url, '_blank');
    }, [project?.url]);

    const crmFloatUrlForLayout = useMemo(() => {
        if (!project?.url) {
            return null;
        }
        const canSeeCrm =
            user?.role === UserRole.SuperAdmin
            || user?.role === UserRole.Admin
            || user?.role === UserRole.ScriptWriter
            || user?.role === UserRole.Manager;
        if (!canSeeCrm) {
            return null;
        }
        if (isHeaderVisible) {
            return null;
        }
        return project.url;
    }, [project?.url, user?.role, isHeaderVisible]);

    useEffect(() => {
        setProjectCrmFloatUrl(crmFloatUrlForLayout);
    }, [crmFloatUrlForLayout, setProjectCrmFloatUrl]);

    useEffect(() => {
        return () => setProjectCrmFloatUrl(null);
    }, [setProjectCrmFloatUrl]);

    useEffect(() => {
        setPageTitle(project?.title ?? null);
        return () => setPageTitle(null);
    }, [project?.title, setPageTitle]);

    useEffect(() => {
        if (!hasUnacknowledgedChanges) {
            return;
        }

        setActivePanels((prevPanels) => {
            if (prevPanels.includes(ProjectDetailTabKey.Regulation)) {
                return prevPanels;
            }

            return [...prevPanels, ProjectDetailTabKey.Regulation];
        });
    }, [hasUnacknowledgedChanges]);

    const projectDetailCardStyles = useMemo(() => {
        if (!isMobileProjectLayout) {
            return undefined;
        }
        return {
            header: {
                paddingTop: 16,
                paddingBottom: 16,
                paddingLeft: 16,
                paddingRight: 16,
                overflow: 'visible',
            },
            title: {
                display: 'block',
                overflow: 'visible',
                whiteSpace: 'normal',
                textOverflow: 'unset',
                width: '100%',
                maxWidth: '100%',
            },
            body: {
                padding: 16,
            },
        };
    }, [isMobileProjectLayout]);

    return (
        <>
            <Helmet>
                <title>Деталі проекту</title>
            </Helmet>

            {hasUnacknowledgedChanges && (
                <ScriptChangesNotification
                    onOpenDetails={handleOpenRegulationFromAlert}
                    onAcknowledge={handleAcknowledgeRegulationChanges}
                    acknowledgeLoading={isAcknowledgingChanges}
                    lastChangedAt={latestChangedAt}
                    title="Увага, зміни"
                    datePrefix="Регламент оновлено"
                    detailsLabel="Переглянути інші зміни"
                    acknowledgeLabel="Підтверджую"
                    description={latestRegulationDiff ? (
                        <div>
                            <div><strong>Було:</strong> {latestRegulationDiff.oldValue}</div>
                            <div><strong>Стало:</strong> {latestRegulationDiff.newValue}</div>
                        </div>
                    ) : undefined}
                    type="warning"
                    showDetailsButton={false}
                />
            )}

            <Card
                loading={isLoading}
                className="project-details-card"
                styles={projectDetailCardStyles}
                title={
                    <div ref={headerRef}>
                        <Flex
                            vertical={isMobileProjectLayout}
                            align={isMobileProjectLayout ? 'stretch' : 'center'}
                            justify="space-between"
                            gap="middle"
                            wrap="wrap"
                            style={{ width: '100%' }}
                        >
                            <Flex align="center" gap="middle" style={{ minWidth: 0, flex: isMobileProjectLayout ? undefined : 1 }}>
                                <Link
                                    to={AppRoute.Projects}
                                    aria-label="Назад"
                                    className="app-action-control app-action-control--icon"
                                    style={getActionControlStyleVars(token, {
                                        variant: 'text',
                                        bordered: false,
                                        iconOnly: true,
                                    })}
                                >
                                    <ArrowLeftOutlined style={{ fontSize: 16 }} />
                                </Link>
                                <Typography.Title
                                    level={4}
                                    ellipsis={false}
                                    style={{ margin: 0, whiteSpace: 'normal', wordBreak: 'break-word' }}
                                >
                                    <span>
                                        {project?.title || 'Деталі проекту'}
                                    </span>
                                </Typography.Title>
                                {project && <ProjectActiveBadge isActive={project.is_active !== false} />}
                            </Flex>
                            <Flex
                                align="center"
                                gap="middle"
                                wrap="wrap"
                                style={{
                                    marginLeft: isMobileProjectLayout ? 0 : 'auto',
                                    justifyContent: isMobileProjectLayout ? 'flex-start' : undefined,
                                }}
                            >
                                <RoleGuard roles={[UserRole.SuperAdmin, UserRole.Admin, UserRole.ScriptWriter, UserRole.Manager]}>
                                    {project?.url && (
                                        <Button
                                            className="crm-pulse-btn"
                                            type="text"
                                            size="middle"
                                            icon={<LinkOutlined style={{ color: token.colorPrimary }} />}
                                            onClick={openCrmUrl}
                                            onMouseEnter={() => setCrmBtnHover(true)}
                                            onMouseLeave={() => setCrmBtnHover(false)}
                                            style={{
                                                border: `1px solid ${token.colorPrimary}`,
                                                color: crmButtonTextColor,
                                                marginRight: token.marginLG,
                                                opacity: crmBtnHover ? 0.88 : 1,
                                                transition: 'opacity 0.3s ease',
                                            }}

                                        >
                                            Перейти на CRM
                                        </Button>
                                    )}
                                </RoleGuard>
                                <RoleGuard roles={[UserRole.SuperAdmin, UserRole.Admin, UserRole.ScriptWriter]}>
                                    <Link
                                        to={`${AppRoute.Projects}/${id}/edit`}
                                        className="app-action-control"
                                        style={getActionControlStyleVars(token, {
                                            variant: 'primary',
                                            size: 'large',
                                        })}
                                    >
                                        <EditOutlined style={{ fontSize: 16 }} />
                                        Редагувати
                                    </Link>
                                </RoleGuard>

                                <RoleGuard roles={[UserRole.SuperAdmin, UserRole.Admin, UserRole.ScriptWriter]}>
                                    {project?.is_active !== false ? (
                                        <Button
                                            size="large"
                                            icon={<StopOutlined />}
                                            onClick={() => setToggleModalOpen(true)}
                                            disabled={isToggling || isLoading}
                                            style={{ borderColor: token.colorError, color: token.colorError }}
                                        >
                                            Відключити
                                        </Button>
                                    ) : (
                                        <Button
                                            size="large"
                                            icon={<CheckCircleOutlined />}
                                            onClick={() => setToggleModalOpen(true)}
                                            disabled={isToggling || isLoading}
                                            style={{ borderColor: token.colorSuccess, color: token.colorSuccess }}
                                        >
                                            Активувати
                                        </Button>
                                    )}
                                </RoleGuard>
                                <RoleGuard roles={[UserRole.SuperAdmin, UserRole.Admin]}>
                                    <Button
                                        danger
                                        size="large"
                                        icon={<DeleteOutlined />}
                                        onClick={handleDelete}
                                        disabled={isDeleting}
                                    >
                                        Видалити
                                    </Button>
                                </RoleGuard>
                            </Flex>
                        </Flex>
                    </div>
                }
            >

                <Flex justify="center" style={{ width: '100%' }}>
                    <div style={{ width: '70%', minWidth: 280 }}>
                        <ConfigProvider
                            theme={{
                                components: {
                                    Collapse: {
                                        headerBg: '#9b2335',
                                        contentBg,
                                        headerPadding: '12px 24px',
                                    },
                                },
                            }}
                        >
                            <Collapse
                                activeKey={activePanels}
                                onChange={(keys) => {
                                    setActivePanels(Array.isArray(keys) ? keys : [keys].filter(Boolean));
                                }}
                                expandIcon={() => null}
                                items={[
                                    {
                                        key: 'regulation',
                                        label: (
                                            <Flex align="center" justify="center" gap="small" style={{ width: '100%' }}>
                                                <FileTextOutlined style={{ color: token.colorWhite }} />
                                                <Typography.Text
                                                    strong
                                                    style={{
                                                        fontSize: 18,
                                                        fontWeight: 'bold',
                                                        color: token.colorWhite,
                                                    }}
                                                >
                                                    Регламент
                                                </Typography.Text>
                                                <DownOutlined
                                                    style={{ color: token.colorWhite, fontSize: 14 }}
                                                    rotate={activePanels.includes('regulation') ? 180 : 0}
                                                />
                                            </Flex>
                                        ),
                                        children: (
                                            <ConfigProvider
                                                theme={{
                                                    algorithm: currentTheme === 'dark' ? AntdTheme.defaultAlgorithm : undefined,
                                                    token: currentTheme === 'dark' ? {
                                                        colorText: lightThemeToken.colorText,
                                                        colorLink: lightThemeToken.colorLink,
                                                    } : undefined,
                                                }}
                                            >
                                                <div
                                                    dangerouslySetInnerHTML={{ __html: project?.regulation || '' }}
                                                    style={{
                                                        padding: '8px 0',
                                                        color: currentTheme === 'dark' ? lightThemeToken.colorText : undefined,
                                                    }}
                                                />
                                            </ConfigProvider>
                                        ),
                                        classNames: {
                                            body: 'wswg-content regulation-content',
                                        },
                                    },
                                ]}
                                style={{ marginBottom: 24 }}
                            />
                        </ConfigProvider>
                    </div>
                </Flex>

                <div>
                    <Flex
                        className="project-details-tabs-row"
                        style={{
                            width: '100%',
                            justifyContent: 'center',
                            gap: token.marginSM,
                            marginBottom: token.marginSM,
                        }}
                        wrap={isMobileProjectLayout ? 'wrap' : 'nowrap'}
                    >
                        <button
                            type="button"
                            onClick={() => setActiveTab(ProjectDetailTabKey.Script)}
                            style={{
                                width: isMobileProjectLayout ? '100%' : '25%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: token.marginXS,
                                padding: `${token.paddingContentVerticalLG}px ${token.paddingContentHorizontalLG}px`,
                                fontSize: token.fontSizeLG,
                                fontWeight: 600,
                                border: `1px solid ${activeTab === ProjectDetailTabKey.Script ? token.colorPrimary : token.colorBorder}`,
                                borderRadius: token.borderRadiusLG,
                                background: activeTab === ProjectDetailTabKey.Script ? token.colorPrimary : token.colorFillTertiary,
                                color: activeTab === ProjectDetailTabKey.Script ? '#FFFFFF' : token.colorText,
                                cursor: 'pointer',
                            }}
                        >
                            <OrderedListOutlined />
                            <span>{ProjectDetailTabLabel[ProjectDetailTabKey.Script]}</span>
                        </button>
                        <button
                            type="button"
                            onClick={() => setActiveTab(ProjectDetailTabKey.CrossSells)}
                            style={{
                                width: isMobileProjectLayout ? '100%' : '25%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: token.marginXS,
                                padding: `${token.paddingContentVerticalLG}px ${token.paddingContentHorizontalLG}px`,
                                fontSize: token.fontSizeLG,
                                fontWeight: 600,
                                border: `1px solid ${activeTab === ProjectDetailTabKey.CrossSells ? token.colorPrimary : token.colorBorder}`,
                                borderRadius: token.borderRadiusLG,
                                background: activeTab === ProjectDetailTabKey.CrossSells ? token.colorPrimary : token.colorFillTertiary,
                                color: activeTab === ProjectDetailTabKey.CrossSells ? '#FFFFFF' : token.colorText,
                                cursor: 'pointer',
                            }}
                        >
                            <ShoppingCartOutlined />
                            <span>{ProjectDetailTabLabel[ProjectDetailTabKey.CrossSells]}</span>
                        </button>
                    </Flex>
                    {activeTab === ProjectDetailTabKey.Script && (
                        <ProjectScriptsTab projectId={id} type="default" />
                    )}
                    {activeTab === ProjectDetailTabKey.CrossSells && (
                        <ProjectScriptsTab projectId={id} type="cross-sale" />
                    )}
                </div>
            </Card>

            <DeleteProjectModal
                open={deleteModalOpen}
                entityName={project?.title}
                entityType="project"
                onConfirm={handleDeleteConfirm}
                onCancel={handleDeleteCancel}
                loading={isDeleting}
            />

            <ConfirmModal
                open={toggleModalOpen}
                confirmTitle={project?.is_active !== false ? 'Відключення проекту' : 'Підключення проекту'}
                confirmMessage={
                    project?.is_active !== false
                        ? `Ви впевнені, що хочете відключити проект "${project?.title}"?`
                        : `Ви впевнені, що хочете підключити проект "${project?.title}"?`
                }
                confirmOkText={project?.is_active !== false ? 'Відключити' : 'Підключити'}
                onConfirm={handleToggleConfirm}
                onCancel={handleToggleCancel}
                loading={isToggling}
            />

            {isManager && hasUnacknowledgedChanges && (
                <ScriptChangesModal
                    open={popupQuery.isMode('changes')}
                    changes={unacknowledgedChanges}
                    onConfirm={handleAcknowledgeRegulationChanges}
                    onCancel={handleCloseChangesModal}
                    loading={isAcknowledgingChanges}
                    entityLabel="проєкті"
                />
            )}
        </>
    );
};
